import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { FieldContext, useFieldContext, useFieldIds } from '@/lib/field-context';

export { cn };
export { Button, buttonVariants } from '@/components/ui/button';
export {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardAction,
  CardContent,
  CardFooter,
} from '@/components/ui/card';
export { Skeleton } from '@/components/ui/skeleton';
export { Label };

/* -------------------------------------------------------------------------- */
/*  Native form controls — kept as plain <input>/<select>/<textarea> so       */
/*  react-hook-form's register() spreads directly onto them; restyled to     */
/*  match the shadcn "nova" input.tsx look exactly.                          */
/* -------------------------------------------------------------------------- */
const fieldBase =
  'h-8 w-full min-w-0 rounded-lg border border-input bg-transparent px-2.5 py-1 text-sm transition-colors outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, id, 'aria-invalid': ariaInvalid, 'aria-describedby': describedBy, ...props }, ref) {
    const field = useFieldContext();
    return (
      <input
        ref={ref}
        id={id ?? field?.id}
        aria-invalid={ariaInvalid ?? field?.invalid}
        aria-describedby={describedBy ?? field?.describedBy}
        className={cn(fieldBase, className)}
        {...props}
      />
    );
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, id, 'aria-invalid': ariaInvalid, 'aria-describedby': describedBy, ...props }, ref) {
    const field = useFieldContext();
    return (
      <textarea
        ref={ref}
        id={id ?? field?.id}
        aria-invalid={ariaInvalid ?? field?.invalid}
        aria-describedby={describedBy ?? field?.describedBy}
        rows={3}
        className={cn(fieldBase, 'h-auto py-1.5', className)}
        {...props}
      />
    );
  },
);

export function Field({
  label,
  error,
  required,
  description,
  children,
}: {
  label: string;
  error?: string;
  required?: boolean;
  description?: string;
  children: ReactNode;
}) {
  const { id, errorId, ctx } = useFieldIds(!!error);
  return (
    <div>
      <Label htmlFor={id} className="mb-1.5 text-[13px]">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      <FieldContext.Provider value={ctx}>{children}</FieldContext.Provider>
      {description && !error && (
        <p className="mt-0.5 text-[13px] text-muted-foreground">{description}</p>
      )}
      {error && (
        <span id={errorId} role="alert" className="mt-1 block text-xs text-destructive">
          {error}
        </span>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page header, semantic status Badge                                        */
/* -------------------------------------------------------------------------- */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="font-heading text-xl font-semibold tracking-[-0.02em] text-foreground">
          {title}
        </h1>
        {description && <p className="mt-0.5 text-[13px] text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

const badgeVariants = cva(
  'inline-flex h-[22px] w-fit shrink-0 items-center justify-center gap-1.5 rounded-md border px-2 text-xs font-medium whitespace-nowrap',
  {
    variants: {
      tone: {
        muted: 'border-border bg-muted/50 text-muted-foreground',
        green: 'border-success/30 bg-success/8 text-success',
        red: 'border-destructive/30 bg-destructive/8 text-destructive',
        amber: 'border-warning/35 bg-warning/8 text-warning',
        blue: 'border-chart-2/35 bg-chart-2/8 text-chart-2',
      },
    },
    defaultVariants: { tone: 'muted' },
  },
);

/* Status tones carry a dot so state is never encoded by color alone. */
const DOT_TONES = new Set(['green', 'red', 'amber']);

export function Badge({
  children,
  tone,
  className,
}: { children: ReactNode; className?: string } & VariantProps<typeof badgeVariants>) {
  return (
    <span className={cn(badgeVariants({ tone }), className)}>
      {DOT_TONES.has(tone ?? '') && (
        <span className="size-1.5 shrink-0 rounded-full bg-current" aria-hidden="true" />
      )}
      {children}
    </span>
  );
}
